'use client'

import { Suspense, useState } from 'react'
import { useSearchParams } from 'next/navigation'
import { createBrowserClient } from '@/lib/supabase'
import { safeRedirectOr } from '@/lib/safe-redirect'
import { signInWithOAuth, signInWithPassword } from '@/lib/data/facades/auth-client'

function LoginForm() {
  const searchParams = useSearchParams()
  // Middleware sets `redirect=<pathname>`; OAuth providers echo `callbackUrl`.
  // Both flow back here and both get sanitized so a crafted `?callbackUrl=//evil.com`
  // can't bounce the user off-site after sign-in.
  const callbackUrl = safeRedirectOr(searchParams.get('redirect') ?? searchParams.get('callbackUrl'))
  const [loading, setLoading] = useState<string | null>(null)
  const [error, setError] = useState('')

  // Dev email/password fallback
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const isDev = process.env.NEXT_PUBLIC_BYPASS_AUTH === 'true'

  async function handleGoogleSignIn() {
    setError('')
    setLoading('google')
    const supabase = createBrowserClient()
    const { error: authError } = await signInWithOAuth(supabase, {
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/auth/callback?callbackUrl=${encodeURIComponent(callbackUrl)}`,
      },
    })
    if (authError) {
      setError(authError.message)
      setLoading(null)
    }
  }

  async function handleAzureSignIn() {
    setError('')
    setLoading('azure')
    const supabase = createBrowserClient()
    const { error: authError } = await signInWithOAuth(supabase, {
      provider: 'azure',
      options: {
        redirectTo: `${window.location.origin}/auth/callback?callbackUrl=${encodeURIComponent(callbackUrl)}`,
        scopes: 'email openid profile',
      },
    })
    if (authError) {
      setError(authError.message)
      setLoading(null)
    }
  }

  async function handleDevSignIn(e: React.FormEvent) {
    e.preventDefault()
    setError('')
    setLoading('dev')
    const supabase = createBrowserClient()
    const { error: authError } = await signInWithPassword(supabase, { email, password })
    if (authError) {
      setError(authError.message)
      setLoading(null)
    } else {
      window.location.href = callbackUrl
    }
  }

  return (
    <div
      className="bg-surface rounded-xl w-[400px] max-w-full border border-border flex flex-col items-center gap-8 shadow-modal"
      style={{ padding: '31px' }}
    >
      <h2 className="text-h2 text-foreground">Sign In</h2>

      <div className="w-full flex flex-col gap-4">
        {/* Google */}
        <button
          type="button"
          onClick={handleGoogleSignIn}
          disabled={loading !== null}
          className="w-full h-14 flex items-center justify-center gap-2 rounded-xl border border-border bg-surface text-secondary-foreground text-btn2 hover:bg-surface-subtle transition-colors disabled:opacity-40"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
          </svg>
          {loading === 'google' ? 'Redirecting...' : 'Sign in with Google'}
        </button>

        {/* Outlook / Microsoft Entra ID */}
        <button
          type="button"
          onClick={handleAzureSignIn}
          disabled={loading !== null}
          className="w-full h-14 flex items-center justify-center gap-2 rounded-xl border border-border bg-surface text-secondary-foreground text-btn2 hover:bg-surface-subtle transition-colors disabled:opacity-40"
        >
          <svg width="20" height="20" viewBox="0 0 21 21" fill="none">
            <rect x="1" y="1" width="9" height="9" fill="#F25022" />
            <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
            <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
            <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
          </svg>
          {loading === 'azure' ? 'Redirecting...' : 'Sign in with Outlook'}
        </button>
      </div>

      {error && (
        <p className="text-b2 text-danger text-center">{error}</p>
      )}

      {/* Dev email/password fallback */}
      {isDev && (
        <form onSubmit={handleDevSignIn} className="w-full flex flex-col gap-3 border-t border-border pt-4">
          <p className="text-b3 text-[var(--sb-color-text-placeholder)] text-center">Dev login (BYPASS_AUTH)</p>
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="dev@sunboundhomes.com"
            className="w-full h-10 rounded-lg border border-border px-3 text-b2 focus:border-ring focus:outline-none"
          />
          <input
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="dev123"
            className="w-full h-10 rounded-lg border border-border px-3 text-b2 focus:border-ring focus:outline-none"
          />
          <button
            type="submit"
            disabled={loading !== null}
            className="w-full h-10 rounded-lg bg-primary text-primary-foreground text-btn2 hover:bg-primary-hover disabled:opacity-40"
          >
            {loading === 'dev' ? 'Signing in...' : 'Dev Sign In'}
          </button>
        </form>
      )}

      <p className="text-b3 text-[var(--sb-color-text-placeholder)] text-center">
        Need Help? Call{' '}
        <a href="tel:623-263-9162" className="underline hover:text-tertiary-foreground transition-colors">
          623-263-9162
        </a>
        , or{' '}
        <a href="mailto:help@sunboundhomes.com" className="underline hover:text-tertiary-foreground transition-colors">
          email us
        </a>
        .
      </p>
    </div>
  )
}

export default function LoginPage() {
  return (
    <div className="min-h-screen flex flex-col bg-blue-950 relative overflow-hidden">
      {/* White glow from bottom — matches admissions app blur effect */}
      <div
        className="pointer-events-none absolute left-0 w-full bg-white opacity-20"
        style={{
          height: '70vh',
          bottom: '-35vh',
          borderRadius: '1512px',
          filter: 'blur(200px)',
        }}
      />

      {/* Center content */}
      <div className="relative z-10 flex-1 flex flex-col items-center justify-center px-4">
        {/* Logo */}
        <div className="mb-10">
          <img src="/logo-white.svg" alt="Sunbound" className="h-8 w-auto" />
        </div>

        <Suspense fallback={
          <div className="bg-surface rounded-xl w-[400px] max-w-full p-8 text-center text-b2 text-[var(--sb-color-text-placeholder)] shadow-modal">
            Loading...
          </div>
        }>
          <LoginForm />
        </Suspense>
      </div>

      {/* Footer */}
      <footer className="relative z-10 py-6 text-center">
        <p className="text-b3 text-muted-foreground">
          &copy; Sunbound {new Date().getFullYear()} &middot; All rights reserved.
        </p>
      </footer>
    </div>
  )
}
